import dayjs from "dayjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useOutletContext } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import Table from "../../../../core/common/dataTable"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import {
  getAllFeesInvoices,
  updateFeeInvoiceItem,
} from "../../../../service/fee-invoices"
import InvoicePrintModal from "./invoicePrintModal"
import SplitInstallmentsModal from "./splitInstallmentsModal"
import { StudentDetailsOutletContext } from "./StudentDetailsLayout"

const StudentFees = () => {
  const { enrollment } = useOutletContext<StudentDetailsOutletContext>()
  const currentSession = useSelector(selectCurrentSession)

  const [pagination, setPagination] = useState({ limit: 10, page: 1 })
  const [searchText, setSearchText] = useState("")
  const [loading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState<any[]>([])
  const [paginationData, setPaginationData] = useState<any>()
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [splitInvoice, setSplitInvoice] = useState<any>(null)
  const [showSplitModal, setShowSplitModal] = useState(false)
  const [editingRowId, setEditingRowId] = useState<string | null>(null)
  const [editData, setEditData] = useState<any>({
    manualDiscount: 0,
  })

  const printRef = useRef<HTMLDivElement>(null)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Fee-Receipt-${selectedInvoice?.invoiceNumber || ""}`,
    onAfterPrint: () => {
      setShowInvoiceModal(false)
      setSelectedInvoice(null)
    },
  })

  const handleGetAllInvoices = useCallback(async () => {
    if (!enrollment?._id || !currentSession?._id) return
    try {
      setLoading(true)
      const res = await getAllFeesInvoices({
        page: pagination.page,
        limit: pagination.limit,
        session: currentSession?._id,
        enrollmentId: enrollment._id,
        searchText,
      })
      setInvoices(res?.data || [])
      setPaginationData(res?.pagination)
    } catch (err) {
      console.error("Failed to fetch invoices", err)
      setInvoices([])
      setPaginationData(undefined)
    } finally {
      setLoading(false)
    }
  }, [
    enrollment?._id,
    currentSession?._id,
    pagination.page,
    pagination.limit,
    searchText,
  ])

  useEffect(() => {
    handleGetAllInvoices()
  }, [handleGetAllInvoices])

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page })
  }

  const handlePrintInvoice = (invoice: any) => {
    setSelectedInvoice(invoice)
    setShowInvoiceModal(true)
  }

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false)
    setSelectedInvoice(null)
  }

  // Only an unpaid invoice with at least one non-tuition fee is eligible --
  // monthly/tuition fees already recur every month and admission fee
  // invoices (no enrollment/student yet) are blocked by the backend.
  const hasSplittableItem = (record: any) =>
    record.status?.toLowerCase() !== "paid" &&
    record.invoiceItems?.some(
      (item: any) => !/tuition/i.test(item.feeGroup?.name || item.feeGroupName || "")
    )

  const handleOpenSplitModal = (invoice: any) => {
    setSplitInvoice(invoice)
    setShowSplitModal(true)
  }

  const handleCloseSplitModal = () => {
    setShowSplitModal(false)
    setSplitInvoice(null)
  }

  const calculateTotalDiscount = (record: any) => {
    const manualDiscount = record.discount?.manual || 0
    const profileDiscount = record.discount?.profile || 0
    return manualDiscount + profileDiscount
  }

  const calculateFinalAmount = (record: any) => {
    const subTotal = record.subTotal || 0
    const fine = record.fine || 0
    const totalDiscount = calculateTotalDiscount(record)
    return subTotal - totalDiscount + fine
  }

  const handleSave = async (record: any) => {
    try {
      await updateFeeInvoiceItem(record._id, record.invoiceItems[0]._id, {
        discount: editData.manualDiscount,
      })
      setEditingRowId(null)
      handleGetAllInvoices() // r
    } catch (error) {
      console.error("Failed to update invoice", error)
    }
  }

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNumber",
      render: (_: string, record: any) => record?.invoiceNumber || "-",
    },
    {
      title: "Fees Group",
      dataIndex: "invoiceItems",
      render: (_: any, record: any) => {
        const feeGroups = record?.invoiceItems?.map(
          (item: any) =>
            item?.feeGroup?.name || item?.feeGroupName || "Other Fee"
        )
        const uniqueFeeGroups = Array.from(new Set(feeGroups))
        return uniqueFeeGroups?.length > 0 ? uniqueFeeGroups.join(", ") : "-"
      },
    },
    {
      title: "Month",
      key: "month",
      render: (_: any, record: any) => {
        const monthsAndTypes = record.invoiceItems
          ?.map((item: any) =>
            item.month && item.month !== "other"
              ? dayjs(item.month).format("MMMM YYYY")
              : item.feeName
          )
          .filter(Boolean)

        if (!monthsAndTypes || monthsAndTypes.length === 0) return "-"

        return monthsAndTypes.join(", ")
      },
    },
    {
      title: "Amount",
      dataIndex: "subTotal",
      render: (val: number) => (typeof val === "number" ? val : "-"),
    },
    {
      title: "Fine",
      dataIndex: "fine",
      render: (val: number) => (typeof val === "number" ? val : "-"),
    },
    {
      title: "Manual Discount",
      render: (_: any, record: any) =>
        editingRowId === record._id ? (
          <input
            type="number"
            className="form-control form-control-sm"
            value={editData.manualDiscount}
            style={{ minWidth: "60px" }}
            onChange={e =>
              setEditData((prev: any) => ({
                ...prev,
                manualDiscount: Number(e.target.value),
              }))
            }
            onKeyDown={e => e.key === "Enter" && handleSave(record)}
          />
        ) : record.discount?.manual > 0 ? (
          record.discount.manual
        ) : (
          "-"
        ),
    },
    {
      title: "Profile Discount",
      render: (_: any, record: any) =>
        record.discount?.profile > 0 ? record.discount.profile : "-",
    },
    {
      title: "Total Discount",
      render: (_: any, record: any) => {
        const totalDiscount = calculateTotalDiscount(record)
        return totalDiscount > 0 ? totalDiscount : "-"
      },
    },
    {
      title: "Total",
      render: (_: any, record: any) => calculateFinalAmount(record),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge ${
            text?.toLowerCase() === "paid"
              ? "badge-soft-success"
              : "badge-soft-danger"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Mode",
      dataIndex: "paymentType",
      render: (text: string) => text || "-",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => {
        return (
          <div className="d-flex align-items-center">
            <button
              className="btn btn-primary btn-sm ms-2"
              onClick={() => handlePrintInvoice(record)}
            >
              Print
            </button>
            {hasSplittableItem(record) && (
              <button
                className="btn btn-outline-primary btn-sm ms-2"
                onClick={() => handleOpenSplitModal(record)}
              >
                Split Into Installments
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Invoices</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                setSearchText={setSearchText}
                dataSource={invoices}
                columns={columns}
                loading={loading}
                pagination={{ ...paginationData, onChange: handleChange }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Print Modal */}
      <InvoicePrintModal
        show={showInvoiceModal}
        onClose={handleCloseInvoiceModal}
        onPrint={handlePrint}
        invoice={selectedInvoice}
        studentDetails={{
          firstName: enrollment?.user.firstName,
          lastName: enrollment?.user.lastName,
          className: enrollment?.class.name,
          grNumber: enrollment?.student.grNumber,
          section: enrollment?.section.name,
        }}
        branch={enrollment?.branch}
        printRef={printRef}
        calculateFinalAmount={calculateFinalAmount}
      />

      {/* Split Into Installments Modal */}
      <SplitInstallmentsModal
        show={showSplitModal}
        onClose={handleCloseSplitModal}
        invoice={splitInvoice}
        onSuccess={handleGetAllInvoices}
      />
    </>
  )
}

export default StudentFees
